import { useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Groups, InsertChart, People, Receipt } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { ProductRouteGuard } from '@/components/ProductRouteGuard';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import * as reports from '@/api/reports';
import { listStudyClients } from '@/api/studyClients';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatCurrency } from '@/lib/formatters';
import { INVOICE_KIND_LABEL, INVOICE_STATUS_COLOR, INVOICE_STATUS_LABEL, IVA_CONDITION_LABEL } from '@/lib/labels';

export const Route = createFileRoute('/_authenticated/reportes/')({
  component: ReportesRoute,
});

const MONTH_LABEL = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];


function ReportesRoute(): JSX.Element {
  return (
    <ProductRouteGuard
      products={['Estudio', 'Empresa']}
      requirePermission="canViewInvoices"
      title="Reportes no disponibles"
      description="Seleccioná un producto fiscal con permisos para ver comprobantes antes de abrir reportes."
    >
      <ReportesPage />
    </ProductRouteGuard>
  );
}

function ReportesPage(): JSX.Element {
  const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');

  const summaryQ = useQuery({
    queryKey: ['reports-summary', currency],
    queryFn: () => reports.getSummary(currency),
  });
  const billingQ = useQuery({
    queryKey: ['reports-billing-monthly', currency],
    queryFn: () => reports.getMonthlyBilling(6, currency),
  });
  const mixQ = useQuery({
    queryKey: ['reports-mix', currency],
    queryFn: () => reports.getTypeMix(currency),
  });
  const clientsQ = useQuery({
    queryKey: ['study-clients-min'],
    queryFn: () => listStudyClients(undefined, 0, 200),
    staleTime: 5 * 60_000,
  });

  // Distribución de cartera por condición IVA (se computa de los clientes del estudio).
  const cartera = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of clientsQ.data?.items ?? []) {
      counts.set(c.condicionIva, (counts.get(c.condicionIva) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [clientsQ.data]);

  const chartData = useMemo(
    () =>
      (billingQ.data?.months ?? []).map((m) => ({
        name: `${MONTH_LABEL[m.month]} ${String(m.year).slice(2)}`,
        neto: m.netTotal,
        count: m.count,
      })),
    [billingQ.data],
  );

  if (summaryQ.isLoading) return <LoadingScreen message="Cargando reportes…" />;

  if (summaryQ.error) {
    return (
      <Box>
        <Header currency={currency} onCurrency={setCurrency} />
        <MuiAlert severity="error">{toApiError(summaryQ.error).message}</MuiAlert>
      </Box>
    );
  }

  const s = summaryQ.data!;
  const activeClients = (clientsQ.data?.items ?? []).filter((c) => c.status === 'Active').length;

  return (
    <Box>
      <Header currency={currency} onCurrency={setCurrency} />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Kpi label={`Facturado · 6 meses (${currency})`} value={formatCurrency(s.netBilledLast6Months, currency)} hint="neto autorizado" icon={<InsertChart color="primary" />} />
        <Kpi label="Comprobantes autorizados" value={String(s.authorizedCount)} hint="históricos" icon={<Receipt color="action" />} />
        <Kpi label="Clientes facturados" value={String(s.distinctCustomers)} hint="distintos receptores" icon={<People color="action" />} />
        <Kpi label="Clientes activos" value={String(activeClients)} hint="en la cartera" icon={<Groups color="action" />} />
      </Grid>

      <Grid container spacing={2}>
        {/* Facturación mensual */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Facturación por mes ({currency})</Typography>
            {chartData.length === 0 ? (
              <EmptyState icon={<InsertChart fontSize="inherit" />} title="Sin datos" description="Todavía no hay comprobantes autorizados en el período." />
            ) : (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} width={70} tickFormatter={(v) => formatCurrency(Number(v), currency)} />
                    <RTooltip formatter={(v) => formatCurrency(Number(v), currency)} />
                    <Bar dataKey="neto" name="Neto" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Distribución de cartera */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Distribución de cartera</Typography>
            {cartera.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Sin clientes en la cartera.</Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {cartera.map(([cond, n]) => (
                  <Stack key={cond} direction="row" justifyContent="space-between">
                    <Typography variant="body2">{IVA_CONDITION_LABEL[cond as keyof typeof IVA_CONDITION_LABEL] ?? cond}</Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{n}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Estado de comprobantes
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" gap={1}>
              {s.byStatus.map((b) => (
                <Chip
                  key={b.status}
                  size="small"
                  color={INVOICE_STATUS_COLOR[b.status]}
                  variant="outlined"
                  label={`${INVOICE_STATUS_LABEL[b.status]}: ${b.count}`}
                />
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Mix por tipo de comprobante */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Mix por tipo de comprobante (últimos 6 meses)</Typography>
            {mixQ.data && mixQ.data.items.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Total ({currency})</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...mixQ.data.items]
                      .sort((a, b) => b.total - a.total)
                      .map((it) => (
                        <TableRow key={it.kind}>
                          <TableCell>{INVOICE_KIND_LABEL[it.kind] ?? it.kind}</TableCell>
                          <TableCell align="right">{it.count}</TableCell>
                          <TableCell align="right">{formatCurrency(it.total, currency)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin comprobantes en el período.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function Header({ currency, onCurrency }: { currency: 'ARS' | 'USD'; onCurrency: (c: 'ARS' | 'USD') => void }): JSX.Element {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
      <Box>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsertChart color="primary" /> Reportes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cartera, facturación y mix de comprobantes del estudio.
        </Typography>
      </Box>
      <ToggleButtonGroup size="small" exclusive value={currency} onChange={(_, v: 'ARS' | 'USD' | null) => v && onCurrency(v)}>
        <ToggleButton value="ARS">ARS</ToggleButton>
        <ToggleButton value="USD">USD</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}

function Kpi({ label, value, hint, icon }: { label: string; value: string; hint: string; icon: JSX.Element }): JSX.Element {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icon}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
            <Typography variant="h6" noWrap>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{hint}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
}
